import * as React from "react";

import { ImageAspectRatio, ImageFitStyle } from "common/generator/components/image-types";
import { CarouselViewStyle } from "./carousel-view-style";
import { isUndefinedish } from "common/Support";

export interface CarouselViewProps {
    readonly index: number;
    readonly size: ImageAspectRatio;
    readonly fitStyle: ImageFitStyle;

    readonly className?: string;
    readonly onStartMove?: (el: HTMLDivElement, touchX: number) => void;
    readonly onMove?: (el: HTMLDivElement, touchX: number, event: Event) => void;
    readonly onEndMove?: (el: HTMLDivElement) => void;
    readonly onClick?: (event: React.MouseEvent) => void;
}

const CarouselView: React.FunctionComponent<CarouselViewProps> = p => {
    const { size, children, index, fitStyle, className, onClick, onStartMove, onMove, onEndMove } = p;

    const ref = React.useRef<HTMLDivElement>();

    const numChildren = React.Children.count(children);

    const onTouchMove = React.useCallback(
        (event: TouchEvent) => {
            const current = ref.current;
            if (isUndefinedish(current)) return;

            onMove?.call(undefined, current, event.touches[0].clientX, event);
        },
        [onMove]
    );

    React.useEffect(() => {
        const current = ref.current;
        if (isUndefinedish(current) || numChildren <= 1) return;

        current.addEventListener("touchmove", onTouchMove, {
            capture: true,
            passive: false,
        });

        function cleanup() {
            current?.removeEventListener("touchmove", onTouchMove, {
                capture: true,
            });
        }
        return cleanup;
    }, [onTouchMove, numChildren]);

    const onMouseDown = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const current = ref.current;
            if (isUndefinedish(current)) return;

            onStartMove?.call(undefined, current, event.clientX);
        },
        [onStartMove]
    );

    const onTouchStart = React.useCallback(
        (event: React.TouchEvent) => {
            const current = ref.current;
            if (isUndefinedish(current)) return;

            onStartMove?.call(undefined, current, event.touches[0].clientX);
        },
        [onStartMove]
    );

    const onMouseMove = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const current = ref.current;
            if (event.buttons !== 1 || isUndefinedish(current)) return;

            onMove?.call(undefined, current, event.clientX, event.nativeEvent);
        },
        [onMove]
    );

    const onMouseUp = React.useCallback(() => {
        const current = ref.current;
        if (isUndefinedish(current)) return;

        onEndMove?.call(undefined, current);
    }, [onEndMove]);

    return (
        <CarouselViewStyle className={className} size={size} fitStyle={fitStyle}>
            <div
                ref={ref as any}
                className="scroller"
                onClick={onClick}
                onTouchStart={numChildren > 1 ? onTouchStart : undefined}
                onMouseDown={numChildren > 1 ? onMouseDown : undefined}
                // onTouchMove={numChildren > 1 ? onTouchMove : undefined}
                onTouchEnd={numChildren > 1 ? onMouseUp : undefined}
                onTouchCancel={numChildren > 1 ? onMouseUp : undefined}
                onMouseMove={numChildren > 1 ? onMouseMove : undefined}
                onMouseUp={numChildren > 1 ? onMouseUp : undefined}
                onMouseLeave={numChildren > 1 ? onMouseUp : undefined}>
                {children}
            </div>
            {numChildren > 1 && (
                <div className="tag" key={index}>
                    {index + 1}/{numChildren}
                </div>
            )}
        </CarouselViewStyle>
    );
};

export default CarouselView;
